import { useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../services/supabase/client';

export function WaitlistPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', designation: '', dob: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      await supabase.from('scents_waitlist').insert({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        designation: form.designation.trim(),
        date_of_birth: form.dob || null,
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] text-white flex flex-col">

      {/* Nav */}
      <nav className="px-6 lg:px-12 py-5">
        <a href="/" className="flex items-center gap-2.5 w-fit">
          <img src="/logo.png" alt="Blurp.ie" className="w-8 h-8 object-contain" />
          <span className="text-[15px] font-medium tracking-tight text-white/60">Blurp.ie</span>
        </a>
      </nav>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">

          {status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">You're on the list!</h2>
              <p className="text-[14px] text-white/40 leading-relaxed mb-8">
                We'll reach out when we're ready to launch the World's First Internet of Scents. Stay tuned.
              </p>
              <a
                href="/"
                className="text-[13px] text-purple-400 hover:text-purple-300 transition-colors"
              >
                ← Back to Blurp.ie
              </a>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header */}
              <div className="mb-10">
                <p className="text-[11px] text-purple-400 tracking-[0.2em] uppercase font-semibold mb-3">Coming Soon</p>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  World's First Internet of Scents
                </h1>
                <p className="text-[14px] text-white/40">
                  Join the waitlist. Be first to experience it.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[11px] text-white/40 mb-1.5 ml-1">
                    Full name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Full name"
                    required
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-white/40 mb-1.5 ml-1">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Email address"
                    required
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-white/40 mb-1.5 ml-1">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. CTO, Researcher, Student"
                    required
                    value={form.designation}
                    onChange={(e) => setForm(f => ({ ...f, designation: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-white/40 mb-1.5 ml-1">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your company or organisation"
                    required
                    value={form.company}
                    onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-white/40 mb-1.5 ml-1">
                    Date of birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={form.dob}
                    onChange={(e) => setForm(f => ({ ...f, dob: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white/70 outline-none focus:border-purple-500/50 transition-colors [color-scheme:dark]"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-[12px] text-red-400">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3.5 mt-2 rounded-lg text-[13px] font-semibold tracking-wide text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50 transition-all duration-300"
                >
                  {status === 'submitting' ? 'Submitting...' : 'Join Waitlist'}
                </button>
              </form>

              <p className="text-center text-[12px] text-white/20 mt-6">
                <a href="/" className="hover:text-white/40 transition-colors">← Back to Blurp.ie</a>
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
